import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { organizationsQueryKeys } from '@/hooks/organization/types'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Organization, UpdateOrganization } from '@/types/Organization'

const updateOrganizationDeprecated = async (
  organization: Organization,
  accessToken: string | undefined | null,
): Promise<void> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }
  const body: UpdateOrganization = {
    description: organization.description ?? '',
    name: organization.name,
    default_opportunity_type: organization.default_opportunity_type,
    set_aside_codes: organization.set_aside_codes,
    naics_codes: organization.naics_codes,
    psc_codes: organization.psc_codes,
    user_ids: organization.users.map((user) => user.id),
  }
  if (organization.website_url !== undefined) {
    body.website_url = organization.website_url
  }
  if (organization.address_line_one !== undefined) {
    body.address_line_one = organization.address_line_one
  }
  if (organization.address_line_two !== undefined) {
    body.address_line_two = organization.address_line_two
  }
  if (organization.city !== undefined) {
    body.city = organization.city
  }
  if (organization.state !== undefined) {
    body.state = organization.state
  }
  if (organization.zip_code !== undefined) {
    body.zip_code = organization.zip_code
  }
  if (organization.unique_entity_id !== undefined) {
    body.unique_entity_id = organization.unique_entity_id
  }
  if (organization.cage_code !== undefined) {
    body.cage_code = organization.cage_code
  }
  if (organization.point_of_contact_name !== undefined) {
    body.point_of_contact_name = organization.point_of_contact_name
  }
  if (organization.point_of_contact_email !== undefined) {
    body.point_of_contact_email = organization.point_of_contact_email
  }
  if (organization.minimum_schedules_relevancy_threshold !== undefined) {
    body.minimum_schedules_relevancy_threshold =
      organization.minimum_schedules_relevancy_threshold
  }
  if (organization.cui_compliance_enabled !== undefined) {
    body.cui_compliance_enabled = organization.cui_compliance_enabled
  }
  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${organization.id}`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'UPDATE_ORGANIZATION_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
}

export const useUpdateOrganization = () => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<
    void,
    Error,
    [string, Organization],
    InfiniteData<PaginatedResponse<Organization>> | undefined
  >({
    mutationFn: ([orgId, organization]) =>
      updateOrganizationDeprecated(organization, user?.accessToken),
    onMutate: async ([orgId, organization]) => {
      const key = organizationsQueryKeys.all()
      await queryClient.cancelQueries({ queryKey: key })

      // snapshot the previous value
      const previousData =
        queryClient.getQueryData<InfiniteData<PaginatedResponse<Organization>>>(
          key,
        )

      // optimistically update
      if (previousData) {
        let updatedPages = previousData.pages.map((page) => {
          return {
            ...page,
            data: page.data.map((o) =>
              o.id === organization.id ? organization : o,
            ),
          }
        })

        const userWasRemoved = !organization.users.some((u) => {
          return u.id === user?.user?.userId
        })

        if (userWasRemoved) {
          updatedPages = updatedPages.map((page) => {
            return {
              ...page,
              data: page.data.filter((o) => o.id !== orgId),
            }
          })
        }

        queryClient.setQueryData<InfiniteData<PaginatedResponse<Organization>>>(
          key,
          { ...previousData, pages: updatedPages },
        )
      }

      // return rollback value
      return previousData
    },
    onError: (err, [orgId, organization], context) => {
      // rollback to the previous value
      const key = organizationsQueryKeys.all()
      queryClient.setQueryData(key, context)
      handleError(err)
    },
    onSuccess: (data, [orgId, organization]) => {
      const key = organizationsQueryKeys.all()
      queryClient.invalidateQueries({ queryKey: key })
      const orgKey = organizationsQueryKeys.organization({
        orgId: orgId as UUID,
      })
      return queryClient.invalidateQueries({ queryKey: orgKey })
    },
  })
}

interface UpdateOrganizationWithIdProps {
  orgId: UUID | ''
}

const updateOrganization = async (
  orgId: UUID | '',
  req: UpdateOrganization,
  accessToken: string | undefined | null,
): Promise<void> => {
  if (!orgId) {
    throw new Error(`Organization ID is required.`)
  }
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }
  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}`,
    {
      method: 'PUT',
      body: JSON.stringify(req),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'UPDATE_ORGANIZATION_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
}

export const useUpdateOrganizationWithId = ({
  orgId,
}: UpdateOrganizationWithIdProps) => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<
    void,
    Error,
    UpdateOrganization,
    { previous: unknown; orgKey: readonly unknown[] }
  >({
    mutationFn: (req) => updateOrganization(orgId, req, user?.accessToken),
    onMutate: async (req) => {
      // Optimistically merge the patch into the cached organization so
      // toggles flip instantly.
      const orgKey = organizationsQueryKeys.organization({
        orgId: orgId as UUID,
      })
      await queryClient.cancelQueries({ queryKey: orgKey })
      const previous = queryClient.getQueryData(orgKey)
      queryClient.setQueryData(
        orgKey,
        (current: Record<string, unknown> | undefined) =>
          current && { ...current, ...req },
      )
      return { previous, orgKey }
    },
    onError: (err, _req, context) => {
      if (context) {
        queryClient.setQueryData(context.orgKey, context.previous)
        void queryClient.invalidateQueries({ queryKey: context.orgKey })
      }
      handleError(err)
    },
    onSuccess: () => {
      const orgKey = organizationsQueryKeys.organization({
        orgId: orgId as UUID,
      })
      // Fire-and-forget: the optimistic merge already reflects the result.
      void queryClient.invalidateQueries({ queryKey: orgKey })
    },
  })
}
